// frontend/src/pages/LayoutEditor.tsx
import { useEffect, useMemo, useRef, useState } from "react";


function isSignedMediaUrl(url: string): boolean {
  return (
    url.includes("X-Goog-Algorithm=") ||
    url.includes("X-Goog-Signature=") ||
    url.includes("X-Amz-Algorithm=") ||
    url.includes("X-Amz-Signature=")
  );
}

function withCacheBust(url: string): string {
  if (!url) return url;
  if (/^https?:\/\//i.test(url)) return url;
  if (isSignedMediaUrl(url)) return url;
  const sep = url.includes("?") ? "&" : "?";
  return `${url}${sep}cb=${Date.now()}`;
}
import { toast } from "react-hot-toast";
import { API_BASE_URL as API_BASE } from "@/config";
import { auth } from "@/lib/firebase";

// ----------------------------- types ---------------------------------------

type FieldMeta = {
  key: string;
  type: "number" | "integer" | "string";
  min?: number;
  max?: number;
  step?: number;
  pattern?: string;
};

const FORMATS = ["9:16", "1:1", "16:9"] as const;

type ExplanationMode = "answer_only" | "full";

type SchemaPayload = {
  schema: Record<string, FieldMeta>;
  defaults: Record<string, unknown>;
  help?: Record<string, string>;
};

type GetLayoutResponse = {
  path: string;
  formats: string[];
  content: Record<string, any>;
  effective: Record<string, any>;
  schema: SchemaPayload;
};

// Prefer configured API base; fallback to same-origin
const apiBase: string = (API_BASE || "").trim() || window.location.origin;

function useLocalStorage(key: string, initial = "") {
  const [v, setV] = useState<string>(() => localStorage.getItem(key) || initial);
  useEffect(() => {
    localStorage.setItem(key, v);
  }, [key, v]);
  return [v, setV] as const;
}

const HELP_FALLBACK: Record<string, string> = {
  wrap_frac_question: "Fraction of frame width used to wrap the question (0–1).",
  wrap_frac_explanation: "Fraction of frame width used to wrap the explanation (0–1).",
  font_question: "Font size for the question block.",
  font_answer: "Font size for the answer line.",
  font_explanation: "Font size for the explanation paragraph.",
  ring_size: "Diameter (px) of the countdown ring.",
  ring_stroke: "Stroke width (px) of the ring outline.",
  number_fontsize: "Font size for the numeric countdown.",
  number_color: "CSS color name or hex for the countdown number.",
  ring_y: "ffmpeg Y expression for ring overlay position.",
  num_y: "ffmpeg Y expression for countdown number position.",
  question_y: "Y-position for the question block.",
  answer_y: "Y-position for the answer block.",
  explanation_y: "Y-position for the explanation block.",
  countdown_seconds: "Seconds the countdown runs before the answer appears.",
  tick_volume: "Volume multiplier for ticking (1.0 = unchanged).",
};

export default function LayoutEditor() {
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [data, setData] = useState<GetLayoutResponse | null>(null);
  const [fmt, setFmt] = useState<(typeof FORMATS)[number]>("9:16");
  const [token, setToken] = useLocalStorage("layout_admin_token");
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  // quick re-render controls (match FormatEditor)
  const [sourceUrl, setSourceUrl] = useState("");
  const [expMode, setExpMode] = useState<ExplanationMode>("answer_only");
  const [renderLoading, setRenderLoading] = useState(false);

  useEffect(() => {
    // Helpful when debugging CORS / base URL issues
    // eslint-disable-next-line no-console
    console.log("🌐 API Base URL:", apiBase);
  }, []);

  const fields: FieldMeta[] = useMemo(() => {
    if (!data) return [];
    return Object.entries(data.schema.schema).map(([k, m]) => {
      const meta = m as FieldMeta;
      const { key: _ignored, ...rest } = meta;
      return { key: k, ...(rest as Omit<FieldMeta, "key">) };
    });
  }, [data]);

  const effective = useMemo(() => data?.effective?.[fmt] || {}, [data, fmt]);
  const overrides = useMemo(() => data?.content?.[fmt] || {}, [data, fmt]);
  function overridesFor(f: (typeof FORMATS)[number]) {
    return (data?.content?.[f] || {}) as Record<string, any>;
  }
  const helpMap = useMemo(() => data?.schema?.help ?? HELP_FALLBACK, [data?.schema?.help]);

  async function load(silent = false): Promise<void> {
    if (!silent) setLoading(true);
    setErr(null);
    try {
      const res = await fetch(`${apiBase}/api/video/layout/`, { credentials: "include" });
      if (!res.ok) throw new Error(`${res.status}: ${await res.text()}`);
      const json = (await res.json()) as GetLayoutResponse;
      setData(json);
      const avail = (json.formats?.length ? json.formats : FORMATS) as string[];
      setFmt((prev) => (avail.includes(prev) ? prev : (avail[0] as any)));
      if (!silent) toast.success("Loaded layout");
    } catch (e: any) {
      setErr(e?.message || String(e));
      if (!silent) toast.error(`Load failed: ${e?.message || e}`);
    } finally {
      if (!silent) setLoading(false);
    }
  }

  useEffect(() => {
    load(true).catch(() => { });
  }, []);

  async function savePartial(next: Record<string, any>): Promise<void> {
    try {
      const res = await fetch(`${apiBase}/api/video/layout/${encodeURIComponent(fmt)}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { "X-Admin-Token": token } : {}),
        },
        body: JSON.stringify(next),
      });
      if (!res.ok) throw new Error(`${res.status}: ${await res.text()}`);
      toast.success(`Saved ${Object.keys(next).join(", ")} for ${fmt}`);
      await load(true);
    } catch (e: any) {
      toast.error(e?.message || String(e));
    }
  }

  async function preview(): Promise<void> {
    try {
      setPreviewLoading(true);
      const res = await fetch(`${apiBase}/api/video/layout/preview`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ fmt }),
      });
      if (!res.ok) throw new Error(`${res.status}: ${await res.text()}`);
      const j = await res.json();
      // add a small cache-buster
      const previewUrl = withCacheBust(url);
      const url = j.url?.includes("?") ? `${j.url}&${cb}` : `${j.url}?${cb}`;
      setPreviewUrl(previewUrl);
      toast.success("Preview ready");
    } catch (e: any) {
      toast.error(e?.message || String(e));
    } finally {
      setPreviewLoading(false);
    }
  }

  async function reRender(): Promise<void> {
    const url = sourceUrl.trim();
    if (!url) {
      toast.error("Please paste a valid source video URL.");
      return;
    }
    if (!/^https?:\/\//i.test(url) && !url.startsWith("/static/")) {
      toast.error("Enter a valid http(s) URL or a /static/... URL.");
      return;
    }

    try {
      setRenderLoading(true);

      const idToken = await auth.currentUser?.getIdToken(true).catch(() => undefined);
      const headers = {
        "Content-Type": "application/json",
        ...(idToken ? { Authorization: `Bearer ${idToken}` } : {}),
      };

      const body = {
        source_url: url,
        video_format: fmt,
        overrides: overridesFor(fmt),
        explanation_mode: expMode,
        tiktok_friendly: fmt === "9:16" || expMode === "answer_only",
      } as const;

      const tryPost = async (endpoint: string) => {
        const res = await fetch(`${apiBase}${endpoint}`, {
          method: "POST",
          headers,
          body: JSON.stringify(body),
        });
        if (!res.ok) throw res;
        return res;
      };

      let resp: Response;
      try {
        // Prefer exact regenerate endpoint
        resp = await tryPost(`/api/video/regenerate`);
      } catch {
        // Fallback to generic generate endpoint
        resp = await tryPost(`/api/video/generate-video`);
      }

      const j = (await resp.json().catch(() => ({}))) as any;
      const newUrl: string | undefined = j.video_url ?? j.url ?? j.videoUrl;
      if (!newUrl) throw new Error("Backend did not return a video URL.");

      // Cache-bust the preview so we don't see stale content
      const previewUrl = withCacheBust(url);
      const preview = newUrl.includes("?") ? `${newUrl}&${cb}` : `${newUrl}?${cb}`;

      toast.success("Rendering started / ready");
      setPreviewUrl(preview);
    } catch (e: any) {
      if (e instanceof Response) {
        // If server returned an error response, show its content
        const text = await e.text().catch(() => "");
        const msg = text || e.statusText || "Server error";
        toast.error(`Re-render failed ${e.status}: ${msg}`);
      } else {
        toast.error(e?.message || "Re-render failed");
      }
    } finally {
      setRenderLoading(false);
    }
  }

  async function resetAllForFormat(): Promise<void> {
    if (!confirm(`Reset ALL overrides for ${fmt} to defaults?`)) return;
    try {
      const res = await fetch(`${apiBase}/api/video/layout/${encodeURIComponent(fmt)}`, {
        method: "DELETE",
        headers: { ...(token ? { "X-Admin-Token": token } : {}) },
      });
      if (!res.ok) throw new Error(`${res.status}: ${await res.text()}`);
      await load(true);
      toast.success(`Reset ${fmt} to defaults`);
    } catch (e: any) {
      toast.error(e?.message || String(e));
    }
  }

  async function resetChangedOnly(): Promise<void> {
    const keys = Object.keys(overrides || {});
    if (!keys.length) {
      toast("No changes to reset");
      return;
    }
    if (!confirm(`Reset ${keys.length} changed field(s) for ${fmt}?`)) return;
    try {
      const qs = new URLSearchParams({ keys: keys.join(",") }).toString();
      const res = await fetch(`${apiBase}/api/video/layout/${encodeURIComponent(fmt)}?${qs}`, {
        method: "DELETE",
        headers: { ...(token ? { "X-Admin-Token": token } : {}) },
      });
      if (!res.ok) throw new Error(`${res.status}: ${await res.text()}`);
      await load(true);
      toast.success(`Reset ${keys.length} field(s)`);
    } catch (e: any) {
      toast.error(e?.message || String(e));
    }
  }

  async function editHelp(key: string): Promise<void> {
    const current = helpMap[key] || "";
    const value = window.prompt(`Help for ${key}`, current);
    if (value == null) return;
    try {
      const res = await fetch(`${apiBase}/api/video/layout/help`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { "X-Admin-Token": token } : {}),
        },
        body: JSON.stringify({ [key]: value }),
      });
      if (!res.ok) throw new Error(`${res.status}: ${await res.text()}`);
      await load(true);
      toast.success("Help updated");
    } catch (e: any) {
      toast.error(e?.message || String(e));
    }
  }

  async function downloadJson(): Promise<void> {
    try {
      const res = await fetch(`${apiBase}/api/video/layout/export`);
      if (!res.ok) throw new Error(`${res.status}: ${await res.text()}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "video_layout.json";
      a.click();
      URL.revokeObjectURL(url);
    } catch (e: any) {
      toast.error(e?.message || String(e));
    }
  }

  async function uploadJson(file: File): Promise<void> {
    try {
      const text = await file.text();
      const payload = JSON.parse(text);
      const res = await fetch(`${apiBase}/api/video/layout/import`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { "X-Admin-Token": token } : {}),
        },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error(`${res.status}: ${await res.text()}`);
      await load(true);
      toast.success("Layout imported");
    } catch (e: any) {
      toast.error(e?.message || String(e));
    }
  }

  function Field({ meta }: { meta: FieldMeta }) {
    const defaultVal = data?.schema.defaults?.[meta.key] as any;
    const val = overrides?.[meta.key] ?? effective?.[meta.key] ?? defaultVal;
    const isNum = meta.type === "number" || meta.type === "integer";
    const [local, setLocal] = useState<any>(val);
    useEffect(() => {
      setLocal(val);
    }, [val]);
    const tooltip = helpMap[meta.key] || "";

    return (
      <div className="card p-4">
        <label className="text-sm font-medium text-slate-700 dark:text-slate-200 flex items-center justify-between" title={tooltip}>
          <span className="flex items-center gap-2">
            {meta.key}
            {tooltip && (
              <span aria-label="help" title={tooltip} className="inline-flex items-center justify-center w-4 h-4 text-xs rounded-full border border-slate-400 text-slate-600 dark:text-slate-300">
                i
              </span>
            )}
            <button type="button" className="text-xs underline ml-1" onClick={() => editHelp(meta.key)} title="Edit help text">
              ✎
            </button>
          </span>
          <span className="text-xs text-slate-500 dark:text-slate-400">default: {String(defaultVal)}</span>
        </label>

        {isNum ? (
          <div className="mt-2 grid grid-cols-1 gap-2 md:grid-cols-[1fr_auto] items-center">
            <input
              type="range"
              min={meta.min}
              max={meta.max}
              step={meta.step ?? (meta.type === "integer" ? 1 : 0.01)}
              value={Number(local)}
              onChange={(e) => setLocal(Number(e.target.value))}
              className="w-full"
              title={tooltip}
            />
            <input
              type="number"
              min={meta.min}
              max={meta.max}
              step={meta.step ?? (meta.type === "integer" ? 1 : 0.01)}
              value={local}
              onChange={(e) =>
                setLocal(
                  e.target.value === ""
                    ? ""
                    : meta.type === "integer"
                      ? parseInt(e.target.value)
                      : parseFloat(e.target.value)
                )
              }
              className="w-28 px-2 py-1"
              title={tooltip}
            />
          </div>
        ) : (
          <input
            type="text"
            value={local}
            onChange={(e) => setLocal(e.target.value)}
            className="mt-2 w-full px-3 py-2"
            placeholder={String(defaultVal ?? "")}
            title={tooltip}
          />
        )}

        <div className="mt-3 flex gap-2">
          <button className="btn btn-secondary" onClick={() => setLocal(defaultVal)}>
            Reset to default
          </button>
          <button className="btn btn-primary" onClick={() => savePartial({ [meta.key]: local })}>
            Save
          </button>
        </div>
      </div>
    );
  }

  const GROUPS: Record<string, string[]> = {
    "Wrap Width": ["wrap_frac_question", "wrap_frac_explanation"],
    Typography: ["font_question", "font_answer", "font_explanation", "number_fontsize", "number_color"],
    Countdown: ["ring_size", "ring_stroke", "countdown_seconds", "tick_volume"],
    Positions: ["ring_y", "num_y", "question_y", "answer_y", "explanation_y"],
  };

  const byKey: Record<string, FieldMeta> = useMemo(() => {
    const m: Record<string, FieldMeta> = {};
    fields.forEach((f) => (m[f.key] = f));
    return m;
  }, [fields]);

  const grouped = useMemo(() => {
    const used = new Set<string>();
    const result: [string, FieldMeta[]][] = [];
    Object.entries(GROUPS).forEach(([label, keys]) => {
      const metas = keys.map((k) => byKey[k]).filter(Boolean) as FieldMeta[];
      metas.forEach((m) => used.add(m.key));
      if (metas.length) result.push([label, metas]);
    });
    const leftovers = fields.filter((m) => !used.has(m.key));
    if (leftovers.length) result.push(["Other", leftovers]);
    return result;
  }, [fields, byKey]);

  if (loading && !data) return <div className="p-6 text-slate-600 dark:text-slate-400">Loading…</div>;
  if (err && !data)
    return (
      <div className="p-6">
        <div className="text-red-600 dark:text-red-300 font-medium">Error: {err}</div>
        <button className="mt-3 btn border border-black/10 dark:border-white/10 hover:bg-black/5 dark:hover:bg-white/10" onClick={() => load()}>
          Retry
        </button>
      </div>
    );
  if (!data) return null;

  return (
    <div className="max-w-5xl mx-auto p-6 space-y-6 text-slate-900 dark:text-slate-100 transition-colors">
      <h1 className="text-2xl font-semibold">Video Layout Editor</h1>

      <div className="card p-4 flex flex-wrap items-center gap-3">
        <label className="text-sm text-slate-600 dark:text-slate-400">Admin token:</label>
        <input
          type="password"
          value={token}
          onChange={(e) => setToken(e.target.value)}
          className="px-3 py-1.5"
          placeholder="X-Admin-Token"
        />
        <span className="text-xs text-slate-500 dark:text-slate-400">
          (optional in dev; required if backend sets LAYOUT_ADMIN_TOKEN)
        </span>
        <div className="ml-auto flex items-center gap-2">
          <button
            className="btn border border-black/10 dark:border-white/10 hover:bg-black/5 dark:hover:bg-white/10"
            onClick={() =>
              load()
                .then(() => toast.success("Reloaded layout"))
                .catch(() => { })
            }
          >
            Reload
          </button>
          <button
            className="btn bg-indigo-600 hover:bg-indigo-700 text-white disabled:opacity-60"
            onClick={preview}
            disabled={previewLoading}
          >
            {previewLoading ? "Rendering…" : "Preview 3s"}
          </button>
          <button className="btn border border-black/10 dark:border-white/10" onClick={downloadJson} title="Download current JSON">
            Download JSON
          </button>
          <input
            ref={fileRef}
            type="file"
            accept="application/json"
            className="hidden"
            onChange={(e) => {
              const file = e.currentTarget.files?.[0];
              if (file) {
                uploadJson(file);
              }
              e.currentTarget.value = ""; // allow re-selecting the same file
            }}
          />
          <button className="btn border border-black/10 dark:border-white/10" onClick={() => fileRef.current?.click()} title="Upload a JSON file to replace the layout">
            Upload JSON
          </button>
        </div>
      </div>

      {/* quick re-render */}
      <div className="card p-4 flex flex-wrap items-center gap-2 mt-2">
        <input
          type="url"
          placeholder="Paste a source video URL…"
          value={sourceUrl}
          onChange={(e) => setSourceUrl(e.currentTarget.value)}
          className="px-3 py-2 min-w-[280px] flex-1"
        />
        <select
          value={fmt}
          onChange={(e) => setFmt(e.currentTarget.value as any)}
          className="px-3 py-2"
          aria-label="Video format"
        >
          {FORMATS.map((f) => (
            <option key={f} value={f}>{f}</option>
          ))}
        </select>
        <select
          value={expMode}
          onChange={(e) => setExpMode(e.currentTarget.value as ExplanationMode)}
          className="px-3 py-2"
          aria-label="Explanation mode"
        >
          <option value="answer_only">Answer only</option>
          <option value="full">Full explanation</option>
        </select>
        <button className="btn bg-indigo-600 hover:bg-indigo-700 text-white disabled:opacity-60" onClick={reRender} disabled={renderLoading}>
          {renderLoading ? "Rendering…" : "Re-render"}
        </button>
      </div>

      <div className="card p-4 flex flex-wrap items-center gap-2 mt-2">
        {FORMATS.map((f) => (
          <button
            key={f}
            onClick={() => setFmt(f)}
            className={[
              "px-3 py-1.5 rounded-full border transition-colors",
              fmt === f
                ? "bg-slate-900 text-white dark:bg-slate-100 dark:text-slate-900"
                : "border-black/10 dark:border-white/10 hover:bg-black/5 dark:hover:bg-white/10",
            ].join(" ")}
          >
            {f}
          </button>
        ))}
        <div className="ml-auto flex gap-2">
          <button
            onClick={resetChangedOnly}
            className="btn border border-amber-300 text-amber-700 hover:bg-amber-50 dark:hover:bg-slate-900"
            title="Clear only the fields you changed for this format"
          >
            Reset changed only
          </button>
          <button
            onClick={resetAllForFormat}
            className="btn border border-red-300 text-red-700 hover:bg-red-50 dark:hover:bg-slate-900"
            title="Clear all overrides for this format and fall back to defaults"
          >
            Reset all (format)
          </button>
        </div>
      </div>

      {grouped.map(([label, metas]) => (
        <section key={label} className="mt-1">
          <h2 className="text-lg font-semibold mb-2">{label}</h2>
          <div className="grid md:grid-cols-2 gap-4">
            {metas.map((m) => (
              <Field key={m.key} meta={m} />
            ))}
          </div>
        </section>
      ))}

      {previewUrl && (
        <div className="card p-4">
          <div className="text-sm text-slate-600 dark:text-slate-400 mb-2">Latest output:</div>
          <video src={previewUrl} controls className="w-full max-w-xl rounded" />
        </div>
      )}

      <div className="text-xs text-slate-500 dark:text-slate-400">
        Tip: String fields accept ffmpeg expressions (e.g., <code>(main_h-overlay_h)*0.30</code>).
      </div>
    </div>
  );
}